import { useState } from "react";
import { History, Pencil } from "lucide-react";

import type { WikiPage as WikiPageData } from "@/api/db/wiki";
import { Card } from "@/components/ui/card";
import { format } from "@/lib/format";
import { MarkdownView } from "@/lib/markdown-view";
import { WikiLabels } from "@/lib/labels/wiki";
import { cn } from "@/lib/utils";

import { WikiEdit } from "./wiki-edit";
import { WikiHistory } from "./wiki-history";

function ActionButton({
	icon: Icon,
	label,
	active,
	onClick,
}: {
	icon: typeof Pencil;
	label: string;
	active?: boolean;
	onClick: () => void;
}) {
	return (
		<button
			type="button"
			onClick={onClick}
			aria-expanded={active}
			className={cn(
				"inline-flex h-11 items-center gap-1.5 rounded-md border px-3 text-xs font-medium transition-colors focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring md:h-auto md:px-2.5 md:py-1.5",
				active
					? "border-primary/30 bg-primary/10 text-primary"
					: "text-muted-foreground hover:bg-background hover:text-foreground",
			)}
		>
			<Icon className="size-3.5" />
			{label}
		</button>
	);
}

export function WikiPageView({ page }: { page: WikiPageData }) {
	const [editing, setEditing] = useState(false);
	const [historyOpen, setHistoryOpen] = useState(false);

	return (
		<Card className="p-5 md:p-6 lg:p-8">
			<article>
				<header className="mb-6 flex flex-wrap items-start justify-between gap-3">
					<div className="min-w-0">
						<h2 className="break-words font-display text-2xl font-semibold tracking-tight text-foreground md:text-3xl">
							{page.title}
						</h2>
						<div className="mt-1 flex flex-col gap-0.5 text-xs text-muted-foreground">
							<span>{WikiLabels.updatedBy(page.updated_by)}</span>
							<span className="font-mono">{format.dateTime(page.updated_at)}</span>
						</div>
					</div>

					<div className="flex shrink-0 items-center gap-2">
						<ActionButton
							icon={Pencil}
							label={WikiLabels.edit}
							active={editing}
							onClick={() => setEditing(true)}
						/>
						<ActionButton
							icon={History}
							label={WikiLabels.history}
							active={historyOpen}
							onClick={() => setHistoryOpen((open) => !open)}
						/>
					</div>
				</header>

				<div className="max-w-prose">
					<MarkdownView md={page.content} />
				</div>

				{historyOpen && <WikiHistory slug={page.slug} onClose={() => setHistoryOpen(false)} />}

				{editing && <WikiEdit page={page} onClose={() => setEditing(false)} />}
			</article>
		</Card>
	);
}
